import { ChangeDetectionStrategy, Component, effect, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateModule } from '@ngx-translate/core';
import { HoleToleranceThreshold, SiteHoleTolerance } from 'src/app/bo/site-hole-tolerance';
import { FonctionsService } from 'src/app/services/fonctions-partagees.service';
import { HoleService } from 'src/app/services/hole.service';
import { UtilisateurService } from 'src/app/services/utilisateur.service';
import { ToleranceBarComponent } from 'src/app/shared/components/tolerance-bar/tolerance-bar.component';

const REQUIRED_METRICS = ['HEAD', 'TOE_POSITION', 'LENGTH', 'STEMMING', 'LINEAR_QUANTITY'];

const DEFAULT_THRESHOLDS: HoleToleranceThreshold[] = [
  { metric: 'HEAD', unit: 'm',           asDesignedMax: 0.5, inToleranceMax: 1 },
  { metric: 'TOE_POSITION', unit: 'm',  asDesignedMax: 1, inToleranceMax: 2 },
  { metric: 'LENGTH', unit: 'm',        asDesignedMax: 0.5, inToleranceMax: 1, asDesignedMin: -0.5, inToleranceMin: -1 },
  { metric: 'STEMMING', unit: 'm',      asDesignedMax: 0.5, inToleranceMax: 1, asDesignedMin: -0.2, inToleranceMin: -0.5 },
  { metric: 'LINEAR_QUANTITY', unit: 'kg/m', asDesignedMax: 0.5, inToleranceMax: 1, asDesignedMin: -0.5, inToleranceMin: -1 },
];

@Component({
  selector: 'app-hole-tolerance',
  templateUrl: './hole-tolerance.component.html',
  styleUrls: ['./hole-tolerance.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, TranslateModule, ToleranceBarComponent],
})
export class HoleToleranceComponent {

  private readonly holeService = inject(HoleService);
  protected readonly utilService = inject(UtilisateurService);
  private readonly fonctionsService = inject(FonctionsService);

  protected formData: Partial<SiteHoleTolerance> = {};
  protected readonly showError = signal(false);
  protected readonly isLoaded = signal(false);
  protected readonly editing = signal(false);
  protected readonly hasChanges = signal(false);

  private savedSnapshot = '';

  constructor() {
    effect(() => {
      const site = this.utilService.currentSite();
      if (site?.siteId) {
        this.loadTolerance(site.siteId);
      } else {
        this.formData = {};
        this.isLoaded.set(true);
      }
    });
  }

  protected isRequired(metric: string): boolean {
    return REQUIRED_METRICS.includes(metric);
  }

  protected startEditing(): void {
    this.savedSnapshot = JSON.stringify(this.formData.thresholds ?? []);
    this.editing.set(true);
    this.hasChanges.set(false);
  }

  protected cancelEditing(): void {
    this.formData = {
      ...this.formData,
      thresholds: JSON.parse(this.savedSnapshot || '[]'),
    };
    this.editing.set(false);
    this.hasChanges.set(false);
    this.showError.set(false);
  }

  protected onValueChange(): void {
    const current = JSON.stringify(this.formData.thresholds);
    this.hasChanges.set(current !== this.savedSnapshot);
  }

  private loadTolerance(siteId: number): void {
    this.isLoaded.set(false);
    this.editing.set(false);
    this.holeService.getSiteHoleTolerance(siteId).subscribe({
      next: result => {
        this.formData = {
          ...result,
          thresholds: result.thresholds.map(t => ({
            ...t,
            unit: DEFAULT_THRESHOLDS.find(d => d.metric === t.metric)?.unit ?? '',
          })),
        };
        this.isLoaded.set(true);
      },
      error: (error: HttpErrorResponse) => {
        if (error.status === 404) {
          this.formData = { siteId, thresholds: [...DEFAULT_THRESHOLDS.map(t => ({ ...t }))] };
        } else {
          this.fonctionsService.launch_toast(false);
        }
        this.isLoaded.set(true);
      },
    });
  }

  protected save(): void {
    const thresholds = this.formData.thresholds ?? [];

    const missingRequired = REQUIRED_METRICS.some(metric => {
      const t = thresholds.find(x => x.metric === metric);
      return !t?.asDesignedMax;
    });

    const intervalInvalid = thresholds.some(t =>
      t.inToleranceMax < t.asDesignedMax ||
      (t.asDesignedMin != null && t.inToleranceMin != null && t.inToleranceMin > t.asDesignedMin)
    );

    if (missingRequired || intervalInvalid) {
      this.showError.set(true);
      return;
    }

    const siteId = this.utilService.currentSite()?.siteId;
    if (!siteId) return;

    this.fonctionsService.loading_screen(true);
    this.holeService.putSiteHoleTolerance(siteId, { ...this.formData, siteId } as SiteHoleTolerance).subscribe({
      next: result => {
        this.formData = { ...result };
        this.showError.set(false);
        this.editing.set(false);
        this.hasChanges.set(false);
        this.fonctionsService.loading_screen(false);
        this.fonctionsService.launch_toast(true);
      },
      error: () => {
        this.fonctionsService.loading_screen(false);
        this.fonctionsService.launch_toast(false);
      },
    });
  }
}
